import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';
import { idWindowPageSchema, type IdWindowPage, type IdWindowParams } from '../id-window';

// ====== Schemas from generated OpenAPI spec ======

const { ManufactureFirestoreResponse, CreateManufactureDto, ManufactureIngredientFirestoreResponse } = schemas;

export const ManufactureResponseSchema = ManufactureFirestoreResponse;
export const CreateManufactureSchema = CreateManufactureDto;
export const ManufactureIngredientSchema = ManufactureIngredientFirestoreResponse;

export const ManufactureComparisonResultSchema = z.object({
  exists: z.boolean(),
  supabaseData: ManufactureResponseSchema.nullable(),
}).passthrough();

// ====== Type Exports ======

export type ManufactureResponse = z.infer<typeof ManufactureResponseSchema>;
export type CreateManufacture = z.infer<typeof CreateManufactureSchema>;
export type ManufactureIngredient = z.infer<typeof ManufactureIngredientSchema>;
export type ManufactureComparisonResult = z.infer<typeof ManufactureComparisonResultSchema>;

/**
 * Create manufactures API factory
 */
export function createManufacturesApi(client: TypedApiClient) {
  return {
    /**
     * Get manufactures with optional filters
     */
    getManufactures: (params?: {
      storeId?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
    }): Promise<ManufactureResponse[]> => {
      return client.get('/manufactures', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(ManufactureResponseSchema),
      });
    },

    /**
     * ATL-1477: one snapshot window of `/manufactures` — see `../id-window.ts`. Use it through
     * `readWholeList`, never directly: a single window is not the list.
     */
    getManufacturesWindow: (
      params: {
      storeId?: string;
      startDate?: string;
      endDate?: string;
      } & IdWindowParams,
    ): Promise<IdWindowPage<z.infer<typeof ManufactureResponseSchema>>> => {
      return client.get('/manufactures/window', {
        params: params as Record<string, unknown>,
        responseSchema: idWindowPageSchema(ManufactureResponseSchema),
      });
    },

    /**
     * Create a new manufacture
     */
    createManufacture: (data: CreateManufacture): Promise<ManufactureResponse> => {
      return client.post('/manufactures', {
        data,
        requestSchema: CreateManufactureSchema,
        responseSchema: ManufactureResponseSchema,
      });
    },

    /**
     * Delete a manufacture
     */
    deleteManufacture: (id: string) => {
      return client.delete(apiPath`/manufactures/${id}`, {
        responseSchema: z.object({ success: z.boolean() }),
      });
    },

    /**
     * Compare manufacture with Firestore for dual-write verification
     */
    compareWithFirestore: (firestoreId: string): Promise<ManufactureComparisonResult> => {
      return client.get(apiPath`/manufactures/compare/${firestoreId}`, {
        responseSchema: ManufactureComparisonResultSchema,
      });
    },
  };
}
